import { format } from "date-fns";

import {
  DEGREE_OF_SNR_LOSS_TYPES,
  HEARING_LOSS_LEVEL,
  HEARING_LOSS_SHAPE,
  HEARING_LOSS_TYPE,
  TYMPANOMETRY_TYPES
} from "constants/audiology";
import { isBlank } from "utils/common";

import { PATIENT_CURRENT_REPORT_OPTIONS } from "../merge-tags-options/patient-current-report.utils";
import { MergeTagsReplaceHandler } from "./merge-tags-values";

export const PATIENT_CURRENT_REPORT_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_CURRENT_REPORT_OPTIONS.APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.currentReport?.appointment?.startDate) return null;

    return format(
      new Date(data.currentReport.appointment.startDate),
      "EEEE MMMM dd, yyyy 'at' hh:mm aaa"
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.currentReport?.appointment) return null;

    return data.currentReport.appointment.appointmentType?.name ?? null;
  },

  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_HEARING_LOSS]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_left
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_HEARING_LOSS]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_right
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_WRS_PRESENTATION_LEVEL_LOSS]: ({
    data
  }) => {
    const percentCorrect =
      data.currentReport?.wordRecognitionReport?.leftEar?.percentCorrect;

    const dbhl = data.currentReport?.wordRecognitionReport?.leftEar?.dbhl;

    if (isBlank(percentCorrect) && isBlank(dbhl)) return null;

    if (isBlank(dbhl)) {
      return `${percentCorrect}%`;
    } else if (isBlank(percentCorrect)) {
      return `${dbhl} dBHL`;
    }

    return `${percentCorrect}% at ${dbhl} dBHL`;
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_WRS_PRESENTATION_LEVEL_LOSS]: ({
    data
  }) => {
    const percentCorrect =
      data.currentReport?.wordRecognitionReport?.rightEar?.percentCorrect;

    const dbhl = data.currentReport?.wordRecognitionReport?.rightEar?.dbhl;

    if (isBlank(percentCorrect) && isBlank(dbhl)) return null;

    if (isBlank(dbhl)) {
      return `${percentCorrect}%`;
    } else if (isBlank(percentCorrect)) {
      return `${dbhl} dBHL`;
    }

    return `${percentCorrect}% at ${dbhl} dBHL`;
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_QUICKSIN_AND_SNR_LOSS]: ({ data }) => {
    const snrLoss = data.currentReport?.speechInNoiseReport?.leftEar?.snrLoss;
    const snrLossDegree =
      data.currentReport?.speechInNoiseReport?.leftEar?.snrLossDegree;

    if (isBlank(snrLoss) && isBlank(snrLossDegree)) return null;

    if (isBlank(snrLossDegree)) {
      return `${snrLoss} dB`;
    } else if (isBlank(snrLoss)) {
      return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss`;
    }

    return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss at ${snrLoss} dB`;
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_QUICKSIN_AND_SNR_LOSS]: ({ data }) => {
    const snrLoss = data.currentReport?.speechInNoiseReport?.rightEar?.snrLoss;
    const snrLossDegree =
      data.currentReport?.speechInNoiseReport?.rightEar?.snrLossDegree;

    if (isBlank(snrLoss) && isBlank(snrLossDegree)) return null;

    if (isBlank(snrLossDegree)) {
      return `${snrLoss} dB`;
    } else if (isBlank(snrLoss)) {
      return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss`;
    }

    return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss at ${snrLoss} dB`;
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_TYMPANOGRAM_TYPE]: ({ data }) => {
    return TYMPANOMETRY_TYPES.getFromCode(
      data.currentReport?.tympanometryReport?.leftEar?.type
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_TYMPANOGRAM_TYPE]: ({ data }) => {
    return TYMPANOMETRY_TYPES.getFromCode(
      data.currentReport?.tympanometryReport?.rightEar?.type
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_HEARING_LOSS_LEVEL]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_left
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_HEARING_LOSS_LEVEL]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_right
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_HEARING_LOSS_TYPE]: ({ data }) => {
    return HEARING_LOSS_TYPE.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_type_left
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_HEARING_LOSS_TYPE]: ({ data }) => {
    return HEARING_LOSS_TYPE.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_type_right
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.LEFT_HEARING_LOSS_SHAPE]: ({ data }) => {
    return HEARING_LOSS_SHAPE.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_shape_left
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.RIGHT_HEARING_LOSS_SHAPE]: ({ data }) => {
    return HEARING_LOSS_SHAPE.getFromCode(
      data.currentReport?.diagnosis?.hearing_loss_shape_right
    );
  },
  [PATIENT_CURRENT_REPORT_OPTIONS.SUMMARY]: ({ data }) => {
    return data.currentReport?.summary;
  }
};
